'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, Children, type ComponentType, type PropsWithChildren, useRef } from 'react';
import { useCalculateLines } from '@shared/utils/hooks/use-calculate-lines';
import { Badge } from '../badge/badge';
import { RenderHTML } from '../render-html/render-html';
import { Card } from './card';
import styles from './carousel-card.module.scss';
export type CarouselCardProps = ButtonHTMLAttributes<HTMLAnchorElement> & ButtonHTMLAttributes<HTMLButtonElement> & {
  actionLabel?: string;
  badgeTitle?: string;
  description?: string;
  headline?: string;
  imageAltText: string;
  ImageComponent?: ComponentType<{
    alt: string;
    className?: string;
    src: string;
  }>;
  imageSrc: string;
  isHighlighted?: boolean;
  LinkComponent?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;
  target: string;
  to?: string;
};
export const CarouselCard = t0 => {
  const $ = _c(56);
  let ImageComponent;
  let LinkComponent;
  let actionLabel;
  let badgeTitle;
  let children;
  let description;
  let headline;
  let imageAltText;
  let imageSrc;
  let onClick;
  let props;
  let t1;
  let target;
  let to;
  if ($[0] !== t0) {
    ({
      actionLabel,
      badgeTitle,
      children,
      description,
      headline,
      imageAltText,
      ImageComponent,
      imageSrc,
      isHighlighted: t1,
      LinkComponent,
      onClick,
      target,
      to,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ImageComponent;
    $[2] = LinkComponent;
    $[3] = actionLabel;
    $[4] = badgeTitle;
    $[5] = children;
    $[6] = description;
    $[7] = headline;
    $[8] = imageAltText;
    $[9] = imageSrc;
    $[10] = onClick;
    $[11] = props;
    $[12] = t1;
    $[13] = target;
    $[14] = to;
  } else {
    ImageComponent = $[1];
    LinkComponent = $[2];
    actionLabel = $[3];
    badgeTitle = $[4];
    children = $[5];
    description = $[6];
    headline = $[7];
    imageAltText = $[8];
    imageSrc = $[9];
    onClick = $[10];
    props = $[11];
    t1 = $[12];
    target = $[13];
    to = $[14];
  }
  const isHighlighted = t1 === undefined ? false : t1;
  let t2;
  if ($[15] !== children) {
    t2 = Children.toArray(children);
    $[15] = children;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const childrenArray = t2;
  const textRef = useRef(null);
  const {
    lineCount
  } = useCalculateLines(textRef);
  const isClickable = onClick !== undefined || to !== undefined;
  let t3;
  if ($[17] !== isHighlighted) {
    t3 = classNames(styles["carousel-card"], {
      [styles["carousel-card--highlighted"]]: isHighlighted
    });
    $[17] = isHighlighted;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== badgeTitle) {
    t4 = badgeTitle && <Badge className={styles["carousel-card__badge"]}>{badgeTitle}</Badge>;
    $[19] = badgeTitle;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== ImageComponent || $[22] !== imageAltText || $[23] !== imageSrc) {
    t5 = ImageComponent && <div className={styles["carousel-card__image-container"]}>{ImageComponent && <ImageComponent alt={imageAltText} className={classNames([styles["carousel-card__image"]])} src={imageSrc} />}</div>;
    $[21] = ImageComponent;
    $[22] = imageAltText;
    $[23] = imageSrc;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== headline) {
    t6 = <h2 className={styles["carousel-card__content-headline"]}>{headline}</h2>;
    $[25] = headline;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const t7 = lineCount > 3;
  const t8 = lineCount > 2;
  const t9 = childrenArray.length === 0;
  let t10;
  if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = classNames(styles["carousel-card__content-description"], {
      [styles["carousel-card__content-description--line-clamp-2"]]: t7,
      [styles["carousel-card__content-description--line-clamp-3"]]: t8,
      [styles["carousel-card__content-description--line-clamp-no-children"]]: t9
    });
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== description || $[32] !== t10) {
    t11 = <RenderHTML className={t10} html={description} />;
    $[31] = description;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== children) {
    t12 = children && <div ref={textRef}>{children}</div>;
    $[34] = children;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t6) {
    t13 = <div className={styles["carousel-card__content"]}>{t6}{t11}{t12}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t6;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== actionLabel) {
    t14 = <div className={styles["carousel-card__footer"]}><span className={styles["carousel-card__footer-action"]}>{actionLabel}</span></div>;
    $[40] = actionLabel;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== isClickable || $[43] !== t13 || $[44] !== t14 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5) {
    t15 = <Card className={t3} isClickable={isClickable}>{t4}{t5}{t13}{t14}</Card>;
    $[42] = isClickable;
    $[43] = t13;
    $[44] = t14;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const content = t15;
  if (isClickable) {
    let t16;
    if ($[49] !== LinkComponent || $[50] !== content || $[51] !== onClick || $[52] !== props || $[53] !== target || $[54] !== to) {
      t16 = to && LinkComponent !== undefined ? <LinkComponent className={styles["carousel-card__action"]} href={to} target={target} {...props}>{content}</LinkComponent> : <button className={styles["carousel-card__action"]} onClick={onClick} type="button" {...props}>{content}</button>;
      $[49] = LinkComponent;
      $[50] = content;
      $[51] = onClick;
      $[52] = props;
      $[53] = target;
      $[54] = to;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    return t16;
  }
  return content;
};